// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import getPagesByParentId from "@awc/App/Handlers/Content/getPagesByParentId";
import { CustomAppState } from "@awc/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@awc/MobiusOverrides/Button/Button";
import Select, { SelectPresentationProps } from "@awc/MobiusOverrides/Select";
import { Tag } from "@awc/Overrides/Pages/Page";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import difference from "lodash/difference";
import intersection from "lodash/intersection";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
    wireCableFilterOptions = "wireCableFilterOptions",
    ulRatingFilterOptions = "ulRatingFilterOptions",
    milSpecFilterOptions = "milSpecFilterOptions",
    rgCableFilterOptions = "rgCableFilterOptions",
    ptfeFilterOptions = "ptfeFilterOptions",
    wireSpecFilterOptions = "wireSpecFilterOptions",
    miscFilterOptions = "miscFilterOptions",
    lmrFilterOptions = "lmrFilterOptions",
}

export interface FilterOption {
    fields: {
        option: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.wireCableFilterOptions]: FilterOption[];
        [fields.ulRatingFilterOptions]: FilterOption[];
        [fields.milSpecFilterOptions]: FilterOption[];
        [fields.rgCableFilterOptions]: FilterOption[];
        [fields.ptfeFilterOptions]: FilterOption[];
        [fields.wireSpecFilterOptions]: FilterOption[];
        [fields.miscFilterOptions]: FilterOption[];
        [fields.lmrFilterOptions]: FilterOption[];
    };
}
// Interfaces & types
interface AuFilter {
    displayName: string;
    options: string[];
    selectedOption: string;
}

interface FilterDefinition {
    fieldKey: string;
    label: string;
}

interface AlliedUnirversityCategoryViewStyles {
    container?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    filtersContainer?: InjectableCss;
    articlesOuterContainer?: InjectableCss;
    articlesContainer?: InjectableCss;
    articleContainer?: InjectableCss;
    articleTitle?: LinkPresentationProps;
    articleInfo?: InjectableCss;
    articleQuickSummaryDisplay?: TypographyPresentationProps;
    articleDifficulty?: TypographyPresentationProps;
    filterByArticleTypeText?: TypographyPresentationProps;
    resetFiltersButton?: ButtonPresentationProps;
    filterSelect?: SelectPresentationProps;
}

type FieldKey =
    | "wireCableFilterOptions"
    | "ulRatingFilterOptions"
    | "milSpecFilterOptions"
    | "rgCableFilterOptions"
    | "ptfeFilterOptions"
    | "wireSpecFilterOptions"
    | "miscFilterOptions"
    | "lmrFilterOptions"
    | "uncategorizedTagFilter";

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// Constants
const selectOption = translate("Select");

const mapStateToProps = (state: CustomAppState) => {
    const parentId = getCurrentPage(state).nodeId;
    return {
        parentId,
        isLoading: state.xPagesContent[parentId]?.isLoading,
        articles: state.xPagesContent[parentId]?.data,
        state,
    };
};
const mapDispatchToProps = {
    getPagesByParentId,
};

export const alliedUnirversityCategoryViewStyles: AlliedUnirversityCategoryViewStyles = {};

const styles = alliedUnirversityCategoryViewStyles;

const filterDefinitions: FilterDefinition[] = [
    { fieldKey: fields.wireCableFilterOptions, label: "Wire & Cable" },
    { fieldKey: fields.ulRatingFilterOptions, label: "UL Rating" },
    { fieldKey: fields.milSpecFilterOptions, label: "Mil-Spec" },
    { fieldKey: fields.rgCableFilterOptions, label: "RG Cable" },
    { fieldKey: fields.ptfeFilterOptions, label: "PTFE" },
    { fieldKey: fields.wireSpecFilterOptions, label: "Wire Specs" },
    { fieldKey: fields.miscFilterOptions, label: "Misc" },
    { fieldKey: fields.lmrFilterOptions, label: "LMR" },
    { fieldKey: "uncategorizedTagFilter", label: "Uncategorized" },
];

const AlliedUniversityCategoryView: FC<Props> = ({ id, fields, isLoading, articles, parentId, getPagesByParentId }) => {
    const [tags, setTags] = useState<Map<string, PageModel[]>>(new Map());
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    useEffect(() => {
        if (!articles && !isLoading) {
            getPagesByParentId({ parentId });
        }
    }, []);

    // Set tags from articles
    useEffect(() => {
        if (!articles) {
            return;
        }
        const tagMap: Map<string, PageModel[]> = new Map();
        articles?.forEach(article => {
            article.generalFields?.articleTags?.forEach((tagItem: Tag) => {
                const tagIdentifier = tagItem.fields.tag;
                if (tagIdentifier) {
                    const existed = tagMap.get(tagIdentifier);
                    tagMap.set(tagIdentifier, existed ? [...existed, article] : [article]);
                }
            });
        });
        setTags(tagMap);
    }, [articles]);

    // set filteredFilters depending on filters provided from CMS and tags
    const filteredFilters = useMemo(() => {
        const newFilteredFilters: Record<string, AuFilter> = {};

        // CMS-defined options
        const cmsOptions: Record<string, string[]> = {};
        for (const { fieldKey } of filterDefinitions) {
            const key = fieldKey as FieldKey;
            if (key !== "uncategorizedTagFilter") {
                cmsOptions[key] =
                    (fields[key] as any)?.map((item: { fields: { option: string } }) => item.fields.option) || [];
            }
        }

        // Track which tags have been categorized
        let categorizedTags: string[] = [];

        // Build filters
        for (const { fieldKey, label } of filterDefinitions) {
            const key = fieldKey as FieldKey;
            if (key === "uncategorizedTagFilter") {
                continue;
            }

            const matchingTags = intersection(Array.from(tags.keys()), cmsOptions[key] || []);
            if (matchingTags.length > 0) {
                newFilteredFilters[key] = {
                    displayName: translate(label),
                    options: [selectOption, ...matchingTags],
                    selectedOption: selectedOptions[key] || selectOption,
                };
                categorizedTags = [...categorizedTags, ...matchingTags];
            }
        }

        // Handle uncategorized tags
        const uncategorizedTags = difference(Array.from(tags.keys()), categorizedTags);
        if (uncategorizedTags.length > 0) {
            newFilteredFilters[filterDefinitions[filterDefinitions.length - 1].fieldKey] = {
                displayName: translate(filterDefinitions[filterDefinitions.length - 1].label),
                options: [selectOption, ...uncategorizedTags],
                selectedOption:
                    selectedOptions[filterDefinitions[filterDefinitions.length - 1].fieldKey] || selectOption,
            };
        }
        return newFilteredFilters;
    }, [fields, tags, selectedOptions, filterDefinitions]);

    // Filter Articles depending on filters selected options
    const filteredArticles = useMemo(() => {
        let newFilteredArticles: PageModel[] = articles;
        for (const { selectedOption } of Object.values(filteredFilters)) {
            if (selectedOption !== selectOption) {
                const tagArticles = tags.get(selectedOption) ?? [];
                newFilteredArticles = intersection(newFilteredArticles, tagArticles);
            }
        }

        return newFilteredArticles;
    }, [articles, filteredFilters, tags]);

    // Handle filter select option change
    const handleChange = (fieldKey: string, selected: string) => {
        setSelectedOptions(prev => ({
            ...prev,
            [fieldKey]: selected,
        }));
    };

    // Handle reset all filters
    const handleReset = () => {
        setSelectedOptions({});
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.leftSection}>
                    <XngZone contentId={id} hint="Left Column Above Filters" zoneName="Left Column Above Filters" />
                    <StyledWrapper {...styles.filtersContainer}>
                        <Typography {...styles.filterByArticleTypeText}>
                            {translate("Filter by article type:")}
                        </Typography>
                        {filterDefinitions.map(({ fieldKey }) => {
                            const filter = filteredFilters[fieldKey];
                            if (!filter || fieldKey === "uncategorizedTagFilter") {
                                return null;
                            }
                            return (
                                <Select
                                    key={fieldKey}
                                    labelPosition="left"
                                    label={filter.displayName}
                                    value={filter?.selectedOption}
                                    onChange={e => handleChange(fieldKey, e.target.value)}
                                    {...styles.filterSelect}
                                >
                                    {filter.options.map((option, index) => (
                                        <option key={index} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </Select>
                            );
                        })}
                        {Object.keys(selectedOptions).length > 0 && (
                            <StyledWrapper>
                                <Button {...styles.resetFiltersButton} onClick={handleReset}>
                                    {translate("Reset Filters")}
                                </Button>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <XngZone contentId={id} hint="Left Column Below Filters" zoneName="Left Column Below Filters" />
                </StyledWrapper>
                <StyledWrapper className="au-list" {...styles.rightSection}>
                    <XngZone contentId={id} hint="AU Category Content" zoneName="AU Category Content" />
                    <StyledWrapper {...styles.articlesContainer}>
                        {filteredArticles?.map(article => {
                            const quickSummaryDisplay = article?.generalFields?.summary
                                ? `${article?.generalFields?.summary.substring(0, 60)}...`
                                : `${article?.generalFields?.articleContents?.substring(0, 60)}...`;

                            const backgroundStyles = `background-image: url(
                                ${article?.generalFields?.articleImage});
                                           background-size: cover;`;

                            const wrapperStyles = {
                                css: css`
                                    ${styles.articleContainer?.css || ""}
                                    ${backgroundStyles}
                                `,
                            };

                            return (
                                <StyledWrapper key={article.id} {...wrapperStyles}>
                                    <StyledWrapper {...styles.articleInfo}>
                                        <Link
                                            aria-label={article.name}
                                            href={
                                                article.generalFields.url ||
                                                Object.values(article?.translatableFields?.urlSegment)?.[0]
                                            }
                                            {...styles.articleTitle}
                                        >
                                            {article.name}
                                        </Link>
                                        {article?.generalFields?.difficulty && (
                                            <Typography {...styles.articleDifficulty}>
                                                {translate("Difficulty: {0}", article?.generalFields?.difficulty)}
                                            </Typography>
                                        )}
                                        {!!quickSummaryDisplay && (
                                            <Typography {...styles.articleQuickSummaryDisplay}>
                                                {parse(quickSummaryDisplay)}
                                            </Typography>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AlliedUniversityCategoryView),
    definition: {
        displayName: "Allied University Category View",
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                displayName: "Default Page Size",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
                isRequired: true,
                min: 1,
            },
            {
                name: fields.wireCableFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.wireSpecFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "Option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.ulRatingFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.rgCableFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.ptfeFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.miscFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.milSpecFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.lmrFilterOptions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.option || "option";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "option",
                        displayName: "Option",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
